// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const AUTHORITY_DIRECTION_STATUSES = ['open', 'closed'];
/**
 * Closure outcomes are the organization's own account of how the direction
 * ended — not an authority determination.
 */
export const DIRECTION_CLOSURE_OUTCOMES = [
    'complied',
    'partially_complied',
    'objected',
    'withdrawn_by_authority',
    'superseded',
];
export const AUTHORITY_RELEASE_DATA_CATEGORIES = [
    'personal_data',
    'customer_data',
    'cross_border_hosted_data',
    'other',
];
/**
 * Where a direction's effective due date came from. Two answers, and the
 * second is never a platform derivation:
 * - `installed_record` — computed by the platform from the installed deadline
 *   record the clock cites (`DirectionClockState.due_at`);
 * - `authority_stated` — the authority's own date, transcribed by a person
 *   (`DirectionClockState.authority_stated_due`).
 */
export const DIRECTION_DUE_SOURCES = ['installed_record', 'authority_stated'];
/**
 * The due date every reminder and overdue count reads (OD11: an authority-stated
 * date drives them exactly as a computed one does), WITH where it came from.
 *
 * A computed `due_at` wins; the authority-stated date is read only on the one
 * state where it may exist — `no_numeric_clock_declared` — so a stale or
 * hand-edited clock carrying both can never let a stated date override a
 * computed one. `null` means undated, and a caller counts it as neither due nor
 * overdue.
 */
export function directionEffectiveDue(clock) {
    if (clock.due_at)
        return { due_at: clock.due_at, source: 'installed_record' };
    const stated = clock.authority_stated_due;
    if (clock.undated_reason === 'no_numeric_clock_declared' &&
        stated &&
        stated.source === 'authority_stated' &&
        typeof stated.due_at === 'string' &&
        stated.due_at.length > 0) {
        return { due_at: stated.due_at, source: 'authority_stated' };
    }
    return null;
}
/**
 * Whether an authority-stated due date may be recorded against this clock —
 * the ONE condition, shared by the service's refusal and the page's offer.
 */
export function acceptsAuthorityStatedDue(clock) {
    return clock.undated_reason === 'no_numeric_clock_declared';
}
export const LEGAL_HOLD_STATUSES = ['active', 'released'];
/**
 * The lifecycle vocabulary of the record itself — what HAPPENED to the
 * account, in the organization's own record-keeping. This is platform
 * vocabulary for event kinds (like attachment owner types or audit actions),
 * not a regulatory enumeration: which windows apply to which events is
 * discovered from installed pack content (AC8), and packs may attach clocks to
 * any of these.
 */
export const REGULATED_ACCOUNT_EVENT_KINDS = [
    'registration',
    'verification',
    're_registration',
    'planned_lock',
    'emergency_lock',
    'restoration',
    'revocation',
    'compromise_notification',
];
// ── Collections ──
/**
 * The record targets the destruction gates actually query — collection AND the
 * id form each gate matches on. A `record` hold scoped outside this set would
 * sit `active` in the register matching nothing, while the UI claimed the
 * platform enforces it; the register refuses those at the boundary instead, and
 * free-described external material remains available for everything else (gate
 * E12.5 pass 1, P1-2).
 */
export const HOLD_ENFORCED_RECORD_TARGETS = [
    {
        collection: 'grc_rights_request_cases',
        /** The gates match a case by its public id, not its internal `_id`. */
        id_field: 'case_public_id',
    },
    {
        collection: 'grc_deletion_orchestration_runs',
        id_field: 'run_id',
    },
    /**
     * Fix sprint 20261016-1956, finding 2 — an access-log entry, by its `_id`.
     * The access-log retention sweep (`AccessLogRetentionService`) puts every
     * row past its window to `findActiveHolds` for this collection, so a hold
     * placed here keeps the row past its retention date until it is released.
     */
    {
        collection: 'grc_access_log',
        id_field: '_id',
    },
];
/**
 * What the place-a-hold dialog can offer to pick from, so an operator chooses a
 * file, a case, a run, an access-log entry or a direction instead of typing an
 * internal id they cannot know. Each source is exactly a scope the service's
 * enforceability check resolves (`platform_file`, the record targets above) or
 * the direction a hold may name; nothing here widens what a hold can reach.
 */
export const HOLD_SCOPE_CANDIDATE_SOURCES = [
    'platform_file',
    ...HOLD_ENFORCED_RECORD_TARGETS.map((t) => t.collection),
    'direction',
];
export const AUTHORITY_DIRECTIONS_COLLECTION = 'grc_authority_directions';
export const LEGAL_HOLDS_COLLECTION = 'grc_legal_holds';
export const REGULATED_ACCOUNTS_COLLECTION = 'grc_regulated_accounts';
export const REGULATED_ACCOUNT_EVENTS_COLLECTION = 'grc_regulated_account_events';
// ── Semantic-policy vocabulary the selector reads (pack-declared) ──
/**
 * The two `event_type` values this module selects on. These are PLATFORM
 * selector vocabulary in the 64.153.0 tradition (`rights_request_*`): packs
 * declare them on deadline records; nothing else in `src/` may branch on a
 * pack's instrument, authority or sla_type strings.
 */
export const DIRECTION_RESPONSE_EVENT_TYPE = 'authority_direction_response';
/** Pack selector for human-selectable authority-event classification kinds. */
export const AUTHORITY_EVENT_CLASSIFICATION_EVENT_TYPE = 'authority_event_classification';
/** Framework-scoped guidance rendered by the Authority Directions register. */
export const AUTHORITY_DIRECTION_BOUNDARY_GUIDANCE_TAG = 'platform_boundary';
export const REGULATED_ACCOUNT_LIFECYCLE_EVENT_TYPE = 'regulated_account_lifecycle';
/**
 * The units a pack uses to mean "on request, with no period stated" — Sprint
 * 62.17.0, decision 3.
 *
 * GENERIC unit vocabulary, in the same class as `hours`, `working_days` and
 * `months`, which `computeDeclaredDue` already names. No instrument, authority
 * or jurisdiction is named here (R-46): these are the two spellings the corpus
 * uses for a duty that arises on a request and states no numeric period.
 *
 * A record whose unit is one of these AND which carries no figure and no
 * absolute date declares a duty, not a clock. The register says so
 * (`no_numeric_clock_declared`) rather than reporting that the platform cannot
 * compute the unit.
 */
export const NO_NUMERIC_CLOCK_UNITS = [
    'upon_request',
    'on_request',
    // Sprint 62.24.0 — "ordered relative to an act, with no length" (the 65.7.0
    // deadline-unit census's own class for these two): a duty that arises
    // before an act and states no numeric period is a duty, not a clock, and
    // reading it as `unsupported_unit` blamed the platform for a period the
    // instrument never stated.
    'before_use',
    'before_transfer',
];
/** Whether a declaring record's unit is one the pack uses to mean "on request". */
export function isNoNumericClockUnit(unit) {
    return typeof unit === 'string' && NO_NUMERIC_CLOCK_UNITS.includes(unit);
}
// ── Error codes ──
export const AUTHORITY_OPERATIONS_ERROR_CODES = {
    DIRECTION_NOT_FOUND: 'AUTHORITY_DIRECTION_NOT_FOUND',
    DIRECTION_CLOSED: 'AUTHORITY_DIRECTION_CLOSED',
    CLASSIFICATION_KIND_NOT_DECLARED: 'AUTHORITY_CLASSIFICATION_KIND_NOT_DECLARED',
    LEGAL_REVIEW_REQUIRED: 'AUTHORITY_DIRECTION_LEGAL_REVIEW_REQUIRED',
    LEGAL_REVIEW_NOT_FOUND: 'AUTHORITY_DIRECTION_LEGAL_REVIEW_NOT_FOUND',
    /**
     * fix-sprint-20260912-2123 F15 — the durable audit entry for a protected-data
     * release (or for a refused release ATTEMPT) did not land, so the act was not
     * performed. A disclosure of protected personal data to an authority that the
     * trail does not record is a disclosure nobody can later account for; the
     * attempt/refusal pair exists precisely to be accounted for. Same shape as
     * `compliance-operations`' `PROOF_AUDIT_UNWRITTEN` (503, nothing persisted).
     */
    DATA_RELEASE_AUDIT_UNWRITTEN: 'AUTHORITY_DIRECTION_DATA_RELEASE_AUDIT_UNWRITTEN',
    REMEDIATION_PLAN_NOT_FOUND: 'AUTHORITY_DIRECTION_REMEDIATION_PLAN_NOT_FOUND',
    /**
     * Sprint 62.26.0 (OD11) — an authority-stated due date is accepted ONLY where
     * the clock resolves to `no_numeric_clock_declared`. On any other state a
     * stated date would either override a computed one or date a clock the
     * platform could not read, and both are refused (409, nothing written).
     */
    STATED_DUE_NOT_ACCEPTED: 'AUTHORITY_DIRECTION_STATED_DUE_NOT_ACCEPTED',
    /**
     * Sprint 62.26.0, gate E12.5 pass 1 (P2-4) — the direction changed between
     * the read a write was built from and the write itself, in a way the write
     * depends on: its receipt moved, or an authority-stated date was recorded or
     * corrected meanwhile. Refused (409, nothing written, no audit, no reminder
     * change) rather than landing a value built from what the record no longer
     * says. A concurrent close is refused as `DIRECTION_CLOSED` and a concurrent
     * re-resolve to a clock that accepts no stated date as
     * `STATED_DUE_NOT_ACCEPTED` — this code is for the remainder.
     */
    DIRECTION_CHANGED_SINCE_READ: 'AUTHORITY_DIRECTION_CHANGED_SINCE_READ',
    HOLD_NOT_FOUND: 'LEGAL_HOLD_NOT_FOUND',
    HOLD_ALREADY_RELEASED: 'LEGAL_HOLD_ALREADY_RELEASED',
    HOLD_SCOPE_NOT_ENFORCEABLE: 'LEGAL_HOLD_SCOPE_NOT_ENFORCEABLE',
    HOLD_BLOCKS_DESTRUCTION: 'LEGAL_HOLD_BLOCKS_DESTRUCTION',
    ACCOUNT_NOT_FOUND: 'REGULATED_ACCOUNT_NOT_FOUND',
    ACCOUNT_EVENT_NOT_FOUND: 'REGULATED_ACCOUNT_EVENT_NOT_FOUND',
    ACCOUNT_EVENT_ALREADY_SUPERSEDED: 'REGULATED_ACCOUNT_EVENT_ALREADY_SUPERSEDED',
    LINK_NOT_FOUND: 'AUTHORITY_OPERATIONS_LINK_NOT_FOUND',
};
//# sourceMappingURL=authority-operations.js.map